import { useState, useEffect } from 'react';
import api from '../services/api';
import { isAfter, isBefore, startOfDay, addDays, differenceInDays } from 'date-fns';

const usePaymentAlerts = (userRole) => {
  const [alertCount, setAlertCount] = useState(0);
  const [fixedExpenses, setFixedExpenses] = useState([]);
  const [loading, setLoading] = useState(false);

  // Verificar se o usuário tem permissão para ver alertas financeiros
  const hasFinancialPermission = userRole === 'administracao' || userRole === 'tesoureiro' || userRole === 'pastor';
  
  // Debug logs temporários
  console.log('=== DEBUG usePaymentAlerts ===');
  console.log('userRole:', userRole);
  console.log('hasFinancialPermission:', hasFinancialPermission);

  // Função para calcular próximo vencimento
  const getNextDueDate = (dueDay) => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    
    // Tenta criar data no mês atual
    let nextDue = new Date(currentYear, currentMonth, dueDay);
    
    // Se já passou, vai para o próximo mês
    if (isBefore(nextDue, startOfDay(today))) {
      nextDue = new Date(currentYear, currentMonth + 1, dueDay);
    }
    
    return nextDue;
  };

  // Função para verificar se foi pago no mês atual
  const isPaidThisMonth = (expense) => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    return expense.paymentHistory?.some(payment => {
      const paymentDate = new Date(payment.paidDate);
      return paymentDate.getMonth() === currentMonth && 
             paymentDate.getFullYear() === currentYear;
    });
  };

  // Carregar despesas fixas
  const loadFixedExpenses = async () => {
    if (!hasFinancialPermission) {
      setAlertCount(0);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get('/fixed-expenses', { params: { includePaymentHistory: true } });
      setFixedExpenses(response.data);
    } catch (error) {
      console.error('Erro ao carregar despesas fixas para alertas:', error);
      setFixedExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  // Calcular alertas
  useEffect(() => {
    if (!hasFinancialPermission || fixedExpenses.length === 0) {
      setAlertCount(0);
      return;
    }

    const today = startOfDay(new Date());
    let alerts = 0;
    
    console.log('Calculando alertas para', fixedExpenses.length, 'despesas');
    console.log('Data atual:', today);
    
    fixedExpenses.forEach(expense => {
      console.log(`\nProcessando despesa: ${expense.name}`);
      // Pular se já foi pago este mês
      if (isPaidThisMonth(expense)) return;
      
      const nextDue = getNextDueDate(expense.dueDay);
      const daysUntilDue = differenceInDays(nextDue, today);
      const alertDays = expense.alertDaysBefore || 3;
      
      // Verificar se precisa de alerta
       if (daysUntilDue <= alertDays) {
         alerts++;
         console.log(`  ⚠️ ALERTA ATIVADO! (${daysUntilDue} <= ${alertDays})`);
       } else {
         console.log(`  ✅ Sem alerta (${daysUntilDue} > ${alertDays})`);
       }
     });
      
      console.log('\n🔔 Total de alertas calculados:', alerts);
      setAlertCount(alerts);
      console.log('AlertCount definido para:', alerts);
  }, [fixedExpenses, hasFinancialPermission]);

  // Carregar dados quando o componente monta ou quando as permissões mudam
  useEffect(() => {
    console.log('useEffect executado - hasFinancialPermission:', hasFinancialPermission);
    
    if (!hasFinancialPermission) {
      console.log('Sem permissão financeira - zerando alertas');
      setAlertCount(0);
      setFixedExpenses([]);
      return;
    }

    console.log('Carregando despesas fixas...');
    loadFixedExpenses();
  }, [hasFinancialPermission]);

  // Ouvir eventos globais para atualizar alertas imediatamente quando uma despesa for marcada como paga
  useEffect(() => {
    const refreshHandler = () => {
      console.log('Evento recebido: refreshPaymentAlerts -> recarregando alertas');
      if (hasFinancialPermission) {
        loadFixedExpenses();
      }
    };
    window.addEventListener('refreshPaymentAlerts', refreshHandler);
    return () => {
      window.removeEventListener('refreshPaymentAlerts', refreshHandler);
    };
  }, [hasFinancialPermission]);

  return {
    alertCount,
    hasFinancialPermission,
    loading,
    fixedExpenses,
    refreshAlerts: loadFixedExpenses
  };
};

export default usePaymentAlerts;
